<script setup lang="ts">
/**
 * Appearance, as a three-position switch: light · system · dark.
 *
 * A two-state light/dark toggle is deliberately not the control, and that is why
 * there are three positions rather than two. "Match my system" has to be a
 * distinct, selectable state, or the third option becomes "whatever you last
 * clicked" and DESIGN.md section 5's `prefers-color-scheme` default is
 * unreachable once the user has touched it. A segmented switch is a switch that
 * can still say "you decide".
 *
 * Native radios inside a `radiogroup`, visually hidden under their own labels:
 * arrow-key navigation, the checked state and the group semantics all arrive for
 * free, where three `aria-pressed` buttons would be three toggles that happen to
 * agree. The thumb is a pseudo-element moved by `:has()` on the checked value,
 * so nothing in script tracks which segment is live.
 *
 * The theme itself changes inside a view transition that grows from the segment
 * the user pressed — see `lib/theme-transition.ts`, which owns every reason not
 * to animate.
 *
 * Two props, because the switch now lives in two places:
 *
 *   - **`tone`.** In the signed-in rail it sits on `--surface-nav`, where the
 *     light-surface palette is unreadable. The signed-out header is still
 *     `--surface-raised`, so the default stays.
 *   - **`orientation`.** Three segments do not fit across the icon-only rail, so
 *     there it stacks — and stacked, they are full 44px cells like everything
 *     else in that rail. A row gets the 40px control height instead, matching
 *     the button beside it, with `.mp-hit` making up the touch floor.
 */

import { nextTick } from 'vue'

import MpIcon from '@/components/base/MpIcon.vue'
import type { IconName } from '@/components/base/icons'
import { revealThemeChange, type RevealOrigin } from '@/lib/theme-transition'
import { THEME_LABELS, type ThemePreference } from '@/lib/theme'
import { useSessionStore } from '@/stores/session'

const props = withDefaults(
  defineProps<{ tone?: 'surface' | 'nav'; orientation?: 'horizontal' | 'vertical' }>(),
  { tone: 'surface', orientation: 'horizontal' }
)

const session = useSessionStore()

/**
 * Display order, which is not `THEME_PREFERENCES`' order. A switch reads as a
 * range, so system belongs between the two ends rather than in front of them —
 * and the thumb offsets in the stylesheet are indexes into this array.
 */
const ORDER: readonly { preference: ThemePreference; icon: IconName }[] = [
  { preference: 'ink-clinical', icon: 'sun' },
  { preference: 'system', icon: 'system' },
  { preference: 'ink-clinical-dark', icon: 'moon' }
]

/** The center of the pressed segment, which is where the reveal grows from. */
function originOf (event: Event): RevealOrigin | null {
  const segment = (event.target as HTMLElement | null)?.closest('label')
  if (!segment) return null
  const box = segment.getBoundingClientRect()
  return { x: box.left + box.width / 2, y: box.top + box.height / 2 }
}

async function choose (preference: ThemePreference, event: Event): Promise<void> {
  const origin = originOf(event)
  await revealThemeChange(async () => {
    await session.setThemePreference(preference)
    // The store applies the theme from a watcher, so the attribute the
    // stylesheet reads is not written until the queue flushes. Without this the
    // transition captures its "after" snapshot before anything has changed and
    // reveals the old theme over itself.
    await nextTick()
  }, origin)
}
</script>

<template>
  <div
    class="mp-theme"
    :class="[`mp-theme--${props.tone}`, `mp-theme--${props.orientation}`]"
    role="radiogroup"
    aria-label="Appearance"
  >
    <label
      v-for="option in ORDER"
      :key="option.preference"
      class="mp-theme__option"
      :class="{ 'mp-hit': props.orientation === 'horizontal' }"
      :data-preference="option.preference"
    >
      <input
        class="mp-sr-only mp-theme__input"
        type="radio"
        name="mp-appearance"
        :value="option.preference"
        :checked="session.themePreference === option.preference"
        @change="choose(option.preference, $event)"
      >
      <MpIcon
        :name="option.icon"
        size="sm"
      />
      <span class="mp-sr-only">{{ THEME_LABELS[option.preference] }}</span>
    </label>
  </div>
</template>

<style scoped>
/* The track. The rules at the bottom only have to say which index is lit. */
.mp-theme {
  --mp-theme-segments: 3;
  position: relative;
  display: flex;
  flex: none;
  padding: var(--space-1);
  border: 1px solid;
  border-radius: var(--radius-pill);
}

.mp-theme::before {
  content: '';
  position: absolute;
  z-index: 0;
  border-radius: var(--radius-pill);
  transition: translate var(--motion-base) var(--motion-ease);
}

.mp-theme--horizontal::before {
  inset-block: var(--space-1);
  inset-inline-start: var(--space-1);
  inline-size: calc((100% - 2 * var(--space-1)) / var(--mp-theme-segments));
}

.mp-theme--vertical {
  flex-direction: column;
}

.mp-theme--vertical::before {
  inset-inline: var(--space-1);
  inset-block-start: var(--space-1);
  block-size: calc((100% - 2 * var(--space-1)) / var(--mp-theme-segments));
}

.mp-theme__option {
  position: relative;
  z-index: 1;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-pill);
  cursor: pointer;
}

/* A row sits beside a 40px button, so the track is 40px too: three segments on
   the small control height plus the track's own 4px. `.mp-hit` on each segment
   grows the *hit* area to the 44px floor in DESIGN.md section 8 without
   growing the box — the same trade the small button makes. */
.mp-theme--horizontal .mp-theme__option {
  min-inline-size: var(--space-8);
  min-block-size: var(--control-h-sm);
}

/* Stacked, `.mp-hit` would be wrong rather than merely unnecessary: each 44px
   overlay would reach 6px into the segment above and below it, and a click near
   a boundary would land on a neighbor. So these are real 44px cells, which is
   what the icon-only rail is made of anyway. */
.mp-theme--vertical .mp-theme__option {
  min-inline-size: var(--target-min);
  min-block-size: var(--target-min);
}

/* The ring goes on the segment the user sees: the input it belongs to is a
   screen-reader-only box one pixel wide. */
.mp-theme__option:has(.mp-theme__input:focus-visible) {
  box-shadow: var(--focus-ring);
}

/* ── Tones ───────────────────────────────────────────────────────────────── */

.mp-theme--surface {
  border-color: var(--border-control);
  background: var(--surface-raised);
  color: var(--text-secondary);
}

.mp-theme--surface::before {
  background: var(--accent-subtle-bg);
}

.mp-theme--surface .mp-theme__option:has(.mp-theme__input:checked) {
  color: var(--accent-subtle-text);
}

.mp-theme--nav {
  border-color: color-mix(in srgb, var(--text-on-nav) 30%, transparent);
  color: color-mix(in srgb, var(--text-on-nav) 65%, transparent);
}

.mp-theme--nav::before {
  background: color-mix(in srgb, var(--text-on-nav) 20%, transparent);
}

/* The lit segment is the full-strength text color over a lightened ground, and
   the unlit ones are held back — so the state is carried by contrast and by the
   thumb's position, not by color alone. DESIGN.md section 8. */
.mp-theme--nav .mp-theme__option:has(.mp-theme__input:checked) {
  color: var(--text-on-nav);
}

.mp-theme--surface .mp-theme__option:hover {
  color: var(--text-primary);
}

.mp-theme--nav .mp-theme__option:hover {
  color: var(--text-on-nav);
}

/* ── Where the thumb sits ────────────────────────────────────────────────── */

.mp-theme--horizontal:has([data-preference='system'] .mp-theme__input:checked)::before {
  translate: 100% 0;
}

.mp-theme--horizontal:has([data-preference='ink-clinical-dark'] .mp-theme__input:checked)::before {
  translate: 200% 0;
}

.mp-theme--vertical:has([data-preference='system'] .mp-theme__input:checked)::before {
  translate: 0 100%;
}

.mp-theme--vertical:has([data-preference='ink-clinical-dark'] .mp-theme__input:checked)::before {
  translate: 0 200%;
}

/* ── The glyphs ──────────────────────────────────────────────────────────────
   Each one arrives the way the thing it stands for does: the sun turns up, the
   moon swings in, the screen just wakes. They run on the rule first matching,
   which is the moment a segment is chosen — and once on load, for the segment
   already chosen, which at `--motion-base` reads as the switch settling rather
   than as something demanding attention.

   `prefers-reduced-motion` is handled centrally: the block at the foot of
   `styles/base.css` collapses every CSS animation to 1ms, which is why these are
   keyframes rather than a script. */
.mp-theme__option .mp-icon {
  transition: scale var(--motion-fast) var(--motion-ease);
}

.mp-theme__option:hover .mp-icon {
  scale: 1.08;
}

.mp-theme__option:has(.mp-theme__input:checked) .mp-icon {
  animation-duration: var(--motion-base);
  animation-timing-function: var(--motion-ease);
}

[data-preference='ink-clinical']:has(.mp-theme__input:checked) .mp-icon {
  animation-name: mp-theme-sunrise;
}

[data-preference='system']:has(.mp-theme__input:checked) .mp-icon {
  animation-name: mp-theme-wake;
}

[data-preference='ink-clinical-dark']:has(.mp-theme__input:checked) .mp-icon {
  animation-name: mp-theme-moonrise;
}

@keyframes mp-theme-sunrise {
  from {
    rotate: -90deg;
    scale: 0.4;
  }
}

@keyframes mp-theme-moonrise {
  from {
    translate: 0 40%;
    rotate: 40deg;
    scale: 0.6;
  }
}

@keyframes mp-theme-wake {
  from {
    scale: 0.6;
    opacity: 0.2;
  }
}
</style>
